import axios from 'axios';

const IS_CUSTOMER_BUILD = process.env.REACT_APP_BUILD_TARGET === 'customer';
const AUDIT_LOGS_BASE = IS_CUSTOMER_BUILD ? '' : `/${['audit', 'logs'].join('-')}`;
const AUDIT_LOGS_EXPORT_PATH = AUDIT_LOGS_BASE ? `${AUDIT_LOGS_BASE}/export` : '';
const AUDIT_LOGS_RETENTION_PATH = AUDIT_LOGS_BASE ? `${AUDIT_LOGS_BASE}/retention` : '';

const api = axios.create({
  // 生产环境一律使用相对 /api，避免硬编码域名/localhost
  baseURL: process.env.NODE_ENV === 'development'
    ? (process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api` : '/api')
    : '/api',
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'application/json; charset=utf-8'
  }
});

// Add a request interceptor if needed for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('token_expires');
      localStorage.removeItem('session_started_at');
      localStorage.removeItem('session_last_activity_at');
      localStorage.removeItem('menu_cache');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 设备管理API
export const deviceAPI = {
  getDevices: (params) => api.get('/devices', { params }),
  getDeviceUsage: () => api.get('/devices/usage'),
  getDevice: (id) => api.get(`/devices/${id}`),
  getDeviceStopCode: (id) => api.get(`/devices/${id}/stop-code`),
  createDevice: (data) => api.post('/devices', data),
  updateDevice: (id, data) => api.put(`/devices/${id}`, data),
  deleteDevice: (id, data) => api.delete(`/devices/${id}`, { data }),
  approveDevice: (id, data) => api.post(`/devices/${id}/approve`, data),
  syncDevice: (id) => api.post(`/devices/${id}/sync`),
  generateUninstallPassword: (id) => api.post(`/devices/${id}/generate-uninstall-password`),
  getDeviceStats: () => api.get('/devices/stats'),
  getDeviceGroups: (params) => api.get('/devices/groups', { params }),
  getDeviceGroupStats: () => api.get('/devices/groups/stats'),
  createDeviceGroup: (data) => api.post('/devices/groups', data),
  updateDeviceGroup: (id, data) => api.put(`/devices/groups/${id}`, data),
  deleteDeviceGroup: (id) => api.delete(`/devices/groups/${id}`),
};

// 策略管理API
export const policyAPI = {
  getPolicies: (params) => api.get('/policies', { params }),
  getPolicy: (id) => api.get(`/policies/${id}`),
  createPolicy: (data) => api.post('/policies', data),
  updatePolicy: (id, data) => api.put(`/policies/${id}`, data),
  deletePolicy: (id) => api.delete(`/policies/${id}`),
  batchDeletePolicies: (ids) => api.post('/policies/batch-delete', { ids }),
  deployPolicy: (id, data) => api.post(`/policies/${id}/deploy`, data),
  getPolicyStats: () => api.get('/policies/stats'),
  getPolicyDetailStats: (id) => api.get(`/policies/${id}/stats`),
  repushPolicy: (id) => api.post(`/policies/${id}/repush`),
};

// 规则管理API
export const ruleAPI = {
  getRules: (params) => api.get('/rules', { params }),
  getRule: (id) => api.get(`/rules/${id}`),
  createRule: (data) => api.post('/rules', data),
  updateRule: (id, data) => api.put(`/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/rules/${id}`),
  testRule: (id, data) => api.post(`/rules/${id}/test`, data),
  getRuleStats: () => api.get('/rules/stats'),
};



// Agent API - 合并所有 Agent 相关功能
export const agentAPI = {
  executeCommand: (data) => api.post('/agent/execute', data),
  getSystemInfo: () => api.get('/agent/system'),
  getProcesses: () => api.get('/agent/processes'),
  getNetworkConnections: () => api.get('/agent/network'),
  getDownloadInfo: () => api.get('/download/info'),
  downloadAgent: (platform, config, params) => {
    return api.post(`/download/agent/${platform}`, config, { 
      params,
      responseType: 'blob',
      timeout: 30000,
    });
  },
  // 新增：GET 下載明文 install.config
  downloadInstallConfig: (params) => api.get('/download/install-config', { 
    params, 
    responseType: 'blob',
    timeout: 15000,
  }),
  getAgents: (params) => api.get('/agents', { params }),
  getAgent: (id) => api.get(`/agents/${id}`),
  createAgent: (data) => api.post('/agents', data),
  updateAgent: (id, data) => api.put(`/agents/${id}`, data),
  deleteAgent: (id) => api.delete(`/agents/${id}`),
  getAgentStats: () => api.get('/agents/stats'),
  sendCommand: (id, command) => api.post(`/agents/${id}/command`, { command }),
};

// 终端控制API
export const terminalAPI = {
  executeCommand: (deviceId, command) => api.post('/terminal/execute', { 
    device_id: deviceId, 
    command 
  }),
  getCommandHistory: (deviceId) => api.get(`/terminal/history/${deviceId}`),
  clearHistory: (deviceId) => api.delete(`/terminal/history/${deviceId}`),
};

// 设备活动与安全日志 API
export const activityAPI = {
  // 设备活动 API
  getActivityLogs: (params) => api.get('/activity/records', { params }),
  getActivityLog: (id) => api.get(`/activity/records/${id}`),
  createActivityLog: (data) => api.post('/activity/records', data),
  updateActivityLog: (id, data) => api.put(`/activity/records/${id}`, data),
  deleteActivityLog: (id) => api.delete(`/activity/records/${id}`),
  batchDeleteActivityLogs: (ids) => api.post('/activity/records/batch-delete', { ids }),
  getActivityLogStats: (params) => api.get('/activity/records/stats', { params }),
  cleanupActivityLogs: (params) => api.post('/activity/records/cleanup', {}, { params }),
  exportActivityLogs: (params) => api.get('/activity/records/export', { params, responseType: 'blob' }),
  
  // 违规日志API (保持兼容性)
  getViolationLogs: (params) => api.get('/activity/violations', { params }),
  getViolationLog: (id) => api.get(`/activity/violations/${id}`),
  processViolationLog: (id, data) => api.post(`/activity/violations/${id}/handle`, data),
  getViolationTrend: (params) => api.get('/activity/violations/trend', { params }),
  
  // 设备日志API
  getDeviceLogs: (params) => api.get('/device-logs', { params }),
  getDeviceLog: (id) => api.get(`/device-logs/${id}`),
  deleteDeviceLog: (id) => api.delete(`/device-logs/${id}`),
  exportDeviceLogs: (params) => api.get('/activity/devices/export', { params, responseType: 'blob' }),
  getDeviceLogsByDevice: (params) => {
    const { device_id, ...otherParams } = params;
    return api.get(`/device-logs/device/${device_id}`, { params: otherParams });
  },
  
  // 审计日志API
  getAuditLogs: (params) => api.get('/activity/audits', { params }),
  
  // 统计和其他API
  getLogStats: (params) => api.get('/activity/violations/stats', { params }),
  cleanupLogs: () => api.post('/activity/cleanup'),
};

export const logAPI = activityAPI;

export const auditLogAPI = IS_CUSTOMER_BUILD ? null : {
  getLogs: (params) => api.get(AUDIT_LOGS_BASE, { params }),
  getLog: (id) => api.get(`${AUDIT_LOGS_BASE}/${id}`),
  exportLogs: (params) => api.get(AUDIT_LOGS_EXPORT_PATH, { params, responseType: 'blob' }),
  getRetentionSettings: () => api.get(AUDIT_LOGS_RETENTION_PATH),
  updateRetentionSettings: (data) => api.put(AUDIT_LOGS_RETENTION_PATH, data),
};

// 用户管理API
export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  changePassword: (data) => api.post('/auth/change-password', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// 用户偏好设置API
export const preferenceAPI = {
  getUserPreferences: () => api.get('/preferences'),
  updateUserPreferences: (data) => api.put('/preferences', data),
  resetUserPreferences: () => api.post('/preferences/reset'),
};

// 系统设置API
export const settingsAPI = {
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
  getSecurity: () => api.get('/settings/security'),
  updateSecurity: (data) => api.put('/settings/security', data),
  regenerateOrganizationStopCode: () => api.post('/settings/security/organization-stop-code/regenerate'),
  getSystemInfo: () => api.get('/settings/system'),
  testConnection: (data) => api.post('/settings/test-connection', data),
  backupDatabase: () => api.post('/settings/backup', {}, { responseType: 'blob' }),
  restoreDatabase: (file) => {
    const formData = new FormData();
    formData.append('backup', file);
    return api.post('/settings/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getLicense: () => api.get('/settings/license'),
  updateLicense: (data) => api.put('/settings/license', data),
};

// 系统配置API
export const configAPI = {
  getConfigs: (params) => api.get('/configs', { params }),
  getConfig: (key) => api.get(`/configs/${key}`),
  getPublicConfigs: () => api.get('/configs/public'),
  createConfig: (data) => api.post('/configs', data),
  updateConfig: (key, data) => api.put(`/configs/${key}`, data),
  deleteConfig: (key) => api.delete(`/configs/${key}`),
};

export const supportPublicAPI = {
  getPublicContactConfig: () => api.get('/public/contact/config'),
  getPublicContactCaptcha: () => api.get('/public/contact/captcha'),
  submitPublicContact: (data) => api.post('/public/contact', data),
  getPublicTicketTracking: (ticketId) => api.get(`/public/contact/tickets/${ticketId}`),
};

// 密码策略管理API
export const passwordPolicyAPI = {
  getPasswordPolicy: () => api.get('/password-policy'),
  updatePasswordPolicy: (data) => api.put('/password-policy', data),
  validatePassword: (data) => api.post('/password-policy/validate', data),
};

// MFA 设置API
export const mfaAPI = {
  // 配置
  getConfig: () => api.get('/auth/mfa/config'),
  updateConfig: (data) => api.put('/auth/mfa/config', data),
  // TOTP 绑定
  startTOTPSetup: () => api.post('/auth/mfa/totp/start'),
  verifyTOTPSetup: (data) => api.post('/auth/mfa/totp/verify', data),
  // 邮箱绑定
  requestEmailSetup: (data) => api.post('/auth/mfa/email/request', data || {}),
  verifyEmailSetup: (data) => api.post('/auth/mfa/email/verify', data),
  // 恢复码
  getRecoveryCount: () => api.get('/auth/mfa/recovery/count'),
  generateRecoveryCodes: () => api.post('/auth/mfa/recovery/generate'),
};

export const apiKeyAPI = {
  create: (data) => api.post('/api-keys', data || {}),
  list: () => api.get('/api-keys'),
  revoke: (id) => api.post(`/api-keys/${id}/revoke`),
};

// 威胁情报API
export const threatIntelligenceAPI = {
  analyzeFileHash: (hash) => api.post(`/threat-intelligence/analyze/${hash}`),
  analyzeActivityLogHashes: (data) => api.post('/threat-intelligence/analyze-activity-records', data),
  getThreatStats: (params) => api.get('/threat-intelligence/stats', { params }),
};

// 仪表板API
export const dashboardAPI = {
  getOverview: () => api.get('/dashboard/overview'),
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivities: (params) => api.get('/dashboard/activities', { params }),
  getAlerts: (params) => api.get('/dashboard/alerts', { params }),
  getTrends: (params) => api.get('/dashboard/trends', { params }),
  getTopThreats: (params) => api.get('/dashboard/threats', { params }),
};

// 系统与健康检查API
export const systemAPI = {
  getHealth: () => api.get('/health'),
};

export { api };
export default api;
